import { timeoutDefaultValue } from "@gik/core/constants";
import type { ISupporter } from "@gik/core/models/gik/ISupporter";
import type { User } from "@gik/core/models/gik/User";
import type { UIComponent } from "@gik/core/types/UI";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { moveCursorToEnd } from "@gik/core/utils/cursor";
import { openIntercomWindow } from "@gik/core/utils/Intercom";
import { renderPortal } from "@gik/core/utils/RenderPortal";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";

import type { PostMedia } from "@gik/stream/types/Stream";
import { Button } from "@gik/ui-legacy/Button";
import type { FormRef, FormSchemaEntry } from "@gik/ui-legacy/Form";
import { Form, FormField, FormGroup, validateForm } from "@gik/ui-legacy/Form";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon";
import { InterrogationRed } from "@gik/ui-legacy/SvgIcon/GikIcons/InterrogationRed";

import React from "react";
import type { SuggestionDataItem } from "react-mentions";
import useLatest from "react-use/lib/useLatest";
import { ThankYouBackgroundPicker } from "../BackgroundPicker/ThankYouBackgroundPicker";
import { ThankYouCard } from "../ThankYouCard/ThankYouCard";

export type ThankYouFormValues = {
	greeting: string;
	message: string;
	signature: string;
	claimId?: string;
	orderId?: string;
	backgroundColor?: string;
};

export type ThankYouFormProps = {
	sender?: User;
	orderId?: string;
	claimId?: string;
	recipient?: Partial<User> | Partial<ISupporter>;
	buttonsPortal?: React.MutableRefObject<HTMLElement>;
	autoFocus?: boolean;
	locked?: boolean;
	value?: string;
	initialValues?: Partial<ThankYouFormValues>;
	attachmentsEdit?: PostMedia[];
	users?: SuggestionDataItem[];
	onSubmit?: (values: ThankYouFormValues) => void | Promise<void>;
	onChange?: (values: ThankYouFormValues) => void;
} & UIComponent;

function ThankYouFormComp({
	sender,
	recipient,
	children,
	className,
	autoFocus,
	buttonsPortal,
	value,
	orderId,
	claimId,
	locked,
	attachmentsEdit: _attachmentsEdit = [],
	users,
	initialValues,
	onSubmit,
	onChange,
	...otherProps
}: React.PropsWithChildren<ThankYouFormProps>): React.ReactElement {
	const bem = useBemCN("thank-you-form");

	const [bg, setBg] = React.useState<string>(initialValues?.backgroundColor);
	const latestBg = useLatest(bg);
	const [error, setError] = React.useState<string>();
	const [submitted, setSubmitted] = React.useState<boolean>();

	// max length for the message
	const maxLengthMessage = 10000;
	// max length for the signature and greeting
	const maxLength = 255;

	const formRef = React.useRef<FormRef>();

	const schema = React.useMemo<FormSchemaEntry[]>(
		(): FormSchemaEntry[] => [
			{
				name: "backgroundColor",
				type: "hidden",
				required: true,
			},
			{
				name: "greeting",
				label: "Greeting",
				type: "text",
				maxLength: maxLength,
				required: true,
				props: {
					maxLengthDisplay: false,
					maxLength: maxLength,
				},
			},
			{
				name: "message",
				label: "Message",
				type: "textarea",
				maxLength: maxLengthMessage,
				required: true,
				props: {
					rows: 6,
					autoGrow: true,
					noresize: true,
					maxLengthDisplay: false,
					maxLength: maxLengthMessage,
				},
			},
			{
				name: "signature",
				label: "Signature",
				type: "text",
				maxLength: maxLength,
				required: true,
				props: {
					maxLengthDisplay: false,
					maxLength: maxLength,
				},
			},
		],
		[],
	);

	const handleChange = React.useCallback(
		async (values: ThankYouFormValues) => {
			setError(undefined);

			const payload: ThankYouFormValues = {
				...values,
				backgroundColor: values.backgroundColor || latestBg.current,
			};
			const errors = await validateForm(schema, payload);

			if (errors.message && errors.message?.type !== "required") {
				setError(errors.message.message);
			}

			onChange?.(payload);
		},
		[schema, latestBg, onChange],
	);

	const handleSubmit = React.useCallback(
		async (values: ThankYouFormValues) => {
			setError(undefined);
			setSubmitted(false);

			const payload: ThankYouFormValues = {
				...values,
				claimId,
				orderId,
				backgroundColor: bg,
			};
			await onSubmit?.(payload);
			setSubmitted(true);
		},
		[bg, claimId, onSubmit, orderId],
	);

	const handleBackgroundSelect = React.useCallback((name: string) => {
		setBg(name);
		// const inputEl = formRef.current?.formEl?.querySelector('.gik-textarea__textarea') as HTMLTextAreaElement;
		// handleChange({ message: inputEl.value as string, backgroundColor: name });
	}, []);

	const handleSubmitAttempt = React.useCallback(
		async (values) => {
			// setError(undefined);
			const errors = await validateForm(schema, values);

			if (errors.message?.type === "required") {
				setError(errors.message.message);
			}
		},
		[schema],
	);

	React.useEffect(() => {
		if (autoFocus) {
			setTimeout(() => {
				const inputEl = formRef.current?.formEl?.querySelector(
					".gik-textarea__textarea",
				) as HTMLTextAreaElement;
				if (inputEl) moveCursorToEnd(inputEl);
			}, timeoutDefaultValue);
		}
	}, [autoFocus]);

	const formId = "postInputForm";
	const styled = bg !== "white" && bg;

	const handleClick = React.useCallback(() => {
		const inputEl = formRef.current?.formEl?.querySelector(
			".gik-textarea__textarea",
		) as HTMLTextAreaElement;
		inputEl?.focus();
	}, []);

	const handleOpenIntercom = React.useCallback(() => {
		openIntercomWindow();
	}, []);

	return (
		<div
			{...bem(null, [{ [bg]: bg }, { styled }, { locked }], className)}
			{...otherProps}
		>
			<Form
				ref={formRef}
				onSubmit={handleSubmit}
				onSubmitAttempt={handleSubmitAttempt}
				onChange={handleChange}
				schema={schema}
				initialValues={initialValues}
				vertical
				id={formId}
				restoreAfterUpdate
				render={({ isSubmitting, isValid, values }) => {
					const buttons = (
						<div {...bem("actions")}>
							<Button
								variant={"default-light"}
								onClick={handleOpenIntercom}
								circle
							>
								<SvgIcon Icon={InterrogationRed} />
							</Button>
							<Button
								type="submit"
								loading={isSubmitting}
								disabled={!isValid || isSubmitting}
								form={formId}
							>
								Send
							</Button>
						</div>
					);

					return (
						<>
							<div {...bem("input")}>
								<div {...bem("preview")}>
									<FormGroup label="PREVIEW" vertical fieldName="preview">
										<ThankYouCard variant={bg} onClick={handleClick}>
											<div {...bem("message-header")}>{values.greeting}</div>
											{values.message}
											<div {...bem("message-footer")}>{values.signature}</div>
										</ThankYouCard>
									</FormGroup>

									<div {...bem("bg-picker", [{ disabled: isSubmitting }])}>
										<ThankYouBackgroundPicker
											value={bg}
											onSelect={handleBackgroundSelect}
										/>
									</div>
								</div>

								<div {...bem("form")}>
									<FormField name="greeting" disabled={isSubmitting} />
									<FormField name="message" disabled={isSubmitting} />
									<FormField name="signature" disabled={isSubmitting} />
								</div>
							</div>
							{renderPortal(buttons, () => buttonsPortal?.current)}
						</>
					);
				}}
			/>
		</div>
	);
}

export const ThankYouForm = withComponentErrorBoundary(ThankYouFormComp);
